<script lang="ts" setup>
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import { ref } from "vue";
import {
  SearchMd,
  ChevronDown,
  Check,
  X,
  LinkExternal02,
} from "utruckin-icons";
import CommonTable from "@/components/common/CommonTable.vue";
import { useUserStore } from "@/features/users/user.store";
import { computed } from "vue";
import { IShiftTemplate } from "@/features/shifts/shifts.types";
import { useShiftStore } from "@/features/shifts/shifts.store";
import { IUser } from "@/features/users/user.types";
import { onMounted } from "vue";
import { watch } from "vue";
import { ITeam } from "@/features/teams/team.types";
import { useTeamStore } from "@/features/teams/team.store";
import { useCheckStore } from "@/features/check/check.store";
import CommonModal from "@/components/common/CommonModal.vue";
import CustomTab from "@/components/custom/CustomTab.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import CustomSearchDropdown from "@/components/custom/CustomSearchDropdown.vue";
import { IRandomSelectionPayload } from "@/features/check/check.types";
import { useNotification } from "@/utils/notification";
import CommonStatus from "@/components/common/CommonStatus.vue";
import { DateTime } from "luxon";
import { TaskStatus } from "@/features/tasks/task.enum";
import CustomQaStatus from "@/views/Check/components/CustomQaStatus.vue";
import { ITaskData } from "@/features/tasks/task.types";
import { onUnmounted } from "vue";
import { Trash01 } from "utruckin-icons";
import router from "@/router";

export interface IModalItem {
  count: number;
  shift_id: number;
  team_id: string;
  user_id: string;
  selection_type: "random_all" | "random_shift" | "random_team" | "random_user";
  date: string;
}

const userStore = useUserStore();
const shiftStore = useShiftStore();
const teamStore = useTeamStore();
const checkStore = useCheckStore();

const { pushNotification } = useNotification();

const searchQuery = ref("");
const loading = ref(false);
const userLoading = ref(false);
const users = ref<IUser[]>([]);
const shifts = ref<IShiftTemplate[]>([]);
const teams = ref<ITeam[]>([]);
const filterUsers = ref<IUser[]>([]);
const reviews = ref<any[]>([]);

const team = ref<string>("");
const user = ref<string>("");

const isModalVisible = ref<boolean>(false);
const modalItem = ref<IModalItem>();

const isCompleteModalVisible = ref<boolean>(false);
const completeModalItem = ref();

let filterTimeout: ReturnType<typeof setTimeout> | null = null;
let searchFilterUserTimer: ReturnType<typeof setTimeout> | null = null;

const collapsedDates = ref<Record<string, boolean>>({});
const collapsedShifts = ref<Record<string, boolean>>({});

const filterStatus = ref(undefined);

const activeTab = ref<number>(0);
const tabs = [
  { label: "All", value: 0 },
  { label: "Shift", value: 1 },
  { label: "Team", value: 2 },
  { label: "User", value: 3 },
];

// infinite loading
const PAGE_SIZE = 50;
const page = ref(1);
const hasMore = ref(true);
const isLoadingMore = ref(false);
const scrollContainer = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const groupedByDate = computed(() => {
  const dateMap = new Map<string, Map<string, any[]>>();

  for (const task of reviews.value) {
    const date = task.started_at
      ? DateTime.fromISO(task.started_at, { zone: "utc" })
          .setZone("America/New_York")
          .toFormat("yyyy-MM-dd")
      : "Unknown";

    const shift = task.task_shift_name ?? "Unknown Shift";

    if (!dateMap.has(date)) {
      dateMap.set(date, new Map());
    }

    const shiftMap = dateMap.get(date)!;

    if (!shiftMap.has(shift)) {
      shiftMap.set(shift, []);
    }

    shiftMap.get(shift)!.push(task);
  }

  return Array.from(dateMap.entries()).map(([date, shiftMap]) => {
    const shiftGroups = Array.from(shiftMap.entries()).map(
      ([label, items]) => ({
        label,
        items,
      })
    );

    return { date, shiftGroups };
  });
});

const teamItems = computed(() =>
  teams.value.map((t) => ({ value: t.id, name: t.name }))
);

const shiftItems = computed(() =>
  shifts.value.map((t) => ({ value: t.id, name: t.name }))
);

const userFilterItems = computed(() =>
  (filterUsers.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
  }))
);

const filterStatusItems = computed(() => {
  return [
    { value: "pending", name: "Pending" },
    { value: "in_progress", name: "In progress" },
    { value: "on_hold", name: "On hold" },
    { value: "completed", name: "Completed" },
  ];
});

const toggleDate = (date: string) => {
  collapsedDates.value[date] = !collapsedDates.value[date];
};

const toggleShift = (date: string, shift: string) => {
  const key = `${date}::${shift}`;
  collapsedShifts.value[key] = !collapsedShifts.value[key];
};

const isDateCollapsed = (date: string) => collapsedDates.value[date];

const isShiftCollapsed = (date: string, shift: string) =>
  collapsedShifts.value[`${date}::${shift}`];

function datepickerToUtcDateOnly(d: string | Date | null | undefined) {
  if (!d) return "";

  const date =
    typeof d === "string"
      ? DateTime.fromISO(d, { zone: "utc" })
      : DateTime.fromJSDate(d, { zone: "utc" });

  return date.setZone("America/New_York").toFormat("LLL d, HH:mm");
}

const formatToEdt = (date: any) => {
  if (!date) return "";

  return date.split("T")[1] == "11:00:00.000Z"
    ? DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d")
    : DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d, HH:mm ZZZZ");
};

const tooltipContent = (list: any[]) =>
  list
    ?.map((item) => `<p>${item.label} - ${formatToEdt(item.date)}</p>`)
    ?.join("");

const formatStatusLabel = (key: string) =>
  key.replace(/_/g, "_").replace(/\b\w/g, (c) => c.toUpperCase());

const getStatus = (qaStatus: string) => {
  const key = (Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>).find(
    (k) => TaskStatus[k] === qaStatus
  );
  return key ? formatStatusLabel(key) : null;
};

const openGenerateModal = () => {
  isModalVisible.value = true;
  modalItem.value = {
    count: 1,
    selection_type: "random_all",
    shift_id: 0,
    team_id: "",
    user_id: "",
    date: "",
  };
};

const closeModal = () => {
  isModalVisible.value = false;
  modalItem.value = undefined;
  userStore.commonFilter.q = "";
};

const closeCompleteModal = () => {
  isCompleteModalVisible.value = false;
  completeModalItem.value = undefined;
};

const searchFilterUser = (val: string) => {
  if (searchFilterUserTimer) clearTimeout(searchFilterUserTimer);
  searchFilterUserTimer = setTimeout(async () => {
    userStore.commonFilter.q = val;
    const [fetchedUsers] = await Promise.all([userStore.getUsers(userLoading)]);

    filterUsers.value = fetchedUsers;
  });
};

const save = async () => {
  if (!modalItem.value) return;

  let random = "random_all";

  if (modalItem.value.shift_id) {
    random = "random_shift";
  }
  if (modalItem.value.team_id) {
    random = "random_team";
  }
  if (modalItem.value.user_id) {
    random = "random_user";
  }

  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");

  const payload: IRandomSelectionPayload = {
    count: modalItem.value.count,
    organization_id: organizationId,
    selection_type: random,
    shift_id: modalItem.value.shift_id ? modalItem.value.shift_id : null,
    team_id: modalItem.value.team_id ? modalItem.value.team_id : null,
    user_id: modalItem.value.user_id ? modalItem.value.user_id : null,
    date: modalItem.value.date ? modalItem.value.date : null,
  };
  await checkStore.createRandomSelection(loading, payload, async (res: any) => {
    if (res) {
      closeModal();
      pushNotification("Successfully generated", "success");
      await resetAndFetch();
    }
  });
};

const fetchReviews = async (opts: { append: boolean }) => {
  checkStore.commonFilterQaReview.page = page.value;
  checkStore.commonFilterQaReview.per_page = PAGE_SIZE;

  const loadingRef = opts.append ? isLoadingMore : loading;

  await checkStore.getManualQaReviews(loading, (data: any) => {
    if (!data) return;

    const items = data.items ?? [];

    reviews.value = opts.append ? [...reviews.value, ...items] : items;

    hasMore.value =
      data.has_more !== undefined ? data.has_more : items.length === PAGE_SIZE;
  });
};

const resetAndFetch = async () => {
  page.value = 1;
  hasMore.value = true;
  await fetchReviews({ append: false });
};

const loadMore = async () => {
  if (loading.value || isLoadingMore.value || !hasMore.value) return;
  page.value += 1;
  await fetchReviews({ append: true });
};

// const getReviews = async () => {
//   await checkStore.getManualQaReviews(loading, (res: any) => {
//     if (res) {
//       reviews.value = res.items;
//     }
//   });
// };

const startQaCheck = async (item: any) => {
  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");

  const filter = {
    organization_id: organizationId,
  };
  if (item.review_status == "pending") {
    await checkStore.startManualQaReview(
      loading,
      item.id,
      filter,
      (res: any) => {
        if (res) {
          pushNotification("Successfully started", "success");
          resetAndFetch();
        }
      }
    );
  }
  if (item.review_status == "on_hold") {
    await checkStore.resumeManualQaReview(
      loading,
      item.id,
      filter,
      (res: any) => {
        if (res) {
          pushNotification("Successfully resumed", "success");
          resetAndFetch();
        }
      }
    );
  }
  if (item.review_status == "in_progress") {
    isCompleteModalVisible.value = true;
    completeModalItem.value = item;
    completeModalItem.value.isCorrect = true;
  }
};

const holdQaCheck = async (item: any) => {
  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");
  const filter = {
    organization_id: organizationId,
  };
  await checkStore.holdManualQaReview(
    loading,
    item.id,
    "for_now",
    filter,
    (res: any) => {
      if (res) {
        pushNotification("Review is now on hold", "warning");
        resetAndFetch();
      }
    }
  );
};

const saveCompleteModal = async () => {
  if (
    completeModalItem.value.isCorrect == false &&
    !completeModalItem?.value.comment?.trim()
  ) {
    return;
  }

  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");

  const filter = {
    organization_id: organizationId,
  };

  await checkStore.completeManualQaReview(
    loading,
    completeModalItem.value.id,
    completeModalItem.value.isCorrect ? "passed" : "failed",
    completeModalItem.value.comment,
    filter,
    (res: any) => {
      if (res) {
        pushNotification("Successfully completed", "success");
        closeCompleteModal();
        resetAndFetch();
      }
    }
  );
};

const availableStatusItems = (item: any) => {
  if (item.review_status == "in_progress") {
    return [{ name: "On hold", value: "on_hold" }];
  }
  return [];
};

const goLink = (e: any, item: any) => {
  e.stopPropagation();
  window.open(item.task_snapshot.eld_link, "_blank")?.focus();
};

const deleteReview = async (e: any, item: any) => {
  e.stopPropagation();

  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");

  const filter = {
    organization_id: organizationId,
  };

  await checkStore.archiveQaReview(loading, item.id, filter, (data: any) => {
    if (data) {
      pushNotification("Successfully archived", "success");
      resetAndFetch();
    }
  });
};

onMounted(async () => {
  const shiftLoading = ref(false);
  const teamLoading = ref(false);

  [users.value, shifts.value, teams.value] = await Promise.all([
    userStore.getUsers(userLoading),
    shiftStore.getShifts(shiftLoading),
    teamStore.getTeams(teamLoading),
  ]);

  await resetAndFetch();

  filterUsers.value = [...users.value];

  if (checkStore.commonFilterQaReview.search) {
    searchQuery.value = checkStore.commonFilterQaReview.search;
  }

  if (checkStore.commonFilterQaReview.team_id) {
    team.value = checkStore.commonFilterQaReview.team_id;
  }

  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    },
    { root: scrollContainer.value, rootMargin: "200px" }
  );
  if (sentinel.value) observer.observe(sentinel.value);
});

watch(
  () => searchQuery.value,
  () => {
    if (loading.value || isLoadingMore.value) return;
    if (filterTimeout) clearTimeout(filterTimeout);
    filterTimeout = setTimeout(async () => {
      checkStore.commonFilterQaReview.search = searchQuery.value;
      await resetAndFetch();
    }, 400);
  }
);

watch(
  () => team.value,
  async (val: string) => {
    if (loading.value || isLoadingMore.value) return;
    checkStore.commonFilterQaReview.team_id = val;
    await resetAndFetch();
  },
  { immediate: false }
);

watch(
  () => user.value,
  async (val: string) => {
    if (loading.value || isLoadingMore.value) return;
    checkStore.commonFilterQaReview.worker_id = val;
    await resetAndFetch();
  },
  { immediate: false }
);

watch(
  () => activeTab.value,
  async (val) => {
    userStore.commonFilter.q = "";
    if (val == 3) {
      filterUsers.value = await userStore.getUsers(loading);
    }
    if (modalItem.value) {
      modalItem.value.shift_id = 0;
      modalItem.value.team_id = "";
      modalItem.value.user_id = "";
    }
  }
);

watch(
  () => filterStatus.value,
  () => {
    checkStore.commonFilterQaReview.review_status = filterStatus.value;
    resetAndFetch();
  }
);

onUnmounted(() => {
  observer?.disconnect();
  checkStore.clearFilter();
});
</script>

<template>
  <div class="v-check" ref="scrollContainer">
    <div class="v-check__header">
      <CommonSearch
        v-model="searchQuery"
        :icon="SearchMd"
        placeholder="Search"
        clearable
      />

      <CommonDropdown
        v-model="team"
        :items="teams.map((t: any) => ({ value: t.id, name: t.name }))"
        label="Team"
        position="bottom"
        dropdownFullWidth
        clearable
      />

      <CustomSearchDropdown
        v-model="user"
        :items="userFilterItems"
        label="User"
        position="bottom"
        clearable
        dropdownFullWidth
        maxWidth="200px"
        @search="searchFilterUser"
      />

      <CommonDropdown
        v-model="filterStatus"
        :items="filterStatusItems"
        label="Status"
        clearable
      />

      <CommonButton
        type="primary"
        label="Generate tasks"
        @clicked="openGenerateModal"
      />
    </div>

    <div class="v-check__body">
      <template
        v-for="(dateGroup, dateIndex) in groupedByDate"
        :key="dateIndex"
      >
        <div
          class="date-row"
          @click="toggleDate(dateGroup.date)"
          :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
        >
          <span class="date-label">{{ dateGroup.date }}</span>
          <span
            class="date-chevron"
            :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
          >
            <ChevronDown :size="20" />
          </span>
        </div>

        <template v-if="!isDateCollapsed(dateGroup.date)">
          <template
            v-for="(shiftGroup, shiftIndex) in dateGroup.shiftGroups"
            :key="shiftIndex"
          >
            <CommonTable
              :headers="checkStore.checkHeaders"
              :groups="[
                {
                  label: shiftGroup.label,
                  items: shiftGroup.items,
                },
              ]"
              :loading="loading"
              :collapsed="isShiftCollapsed(dateGroup.date, shiftGroup.label)"
              @toggle-group="toggleShift(dateGroup.date, shiftGroup.label)"
              :dateIndex="dateIndex"
              :shiftIndex="shiftIndex"
              :isAdmin="isAdmin"
              :withoutHeader="shiftIndex > 0"
            >
              <template #id="{ index }">
                <span>{{ index + 1 }}</span>
              </template>
              <template #company="{ item }">
                <div class="company__name">
                  <span>
                    {{ item.task_company_name }}
                  </span>
                  <span class="app">
                    {{
                      item.task_snapshot.provider_name?.split(" ")[0] || "Hos"
                    }}
                  </span>
                </div>
              </template>
              <template #drivers="{ item }">
                <span>{{ item.task_driver_name }}</span>
              </template>
              <template #users="{ item }">
                <span>{{ item.task_worker_name }}</span>
              </template>
              <template #date="{ item }">
                <span>{{ datepickerToUtcDateOnly(item.created_time) }}</span>
              </template>
              <template #check-date="{ item }">
                <span>{{
                  datepickerToUtcDateOnly(item.task_snapshot.completed_time)
                }}</span>
              </template>
              <template #status="{ item }">
                <CommonStatus
                  v-if="item.task_status && getStatus(item.task_status)"
                  :status="getStatus(item.task_status)!"
                  :tooltipContent="
                    tooltipContent(item.qa_failed_reason_details)
                  "
                />
                <span v-else>—</span>
              </template>

              <template #check="{ item }">
                <CustomQaStatus
                  :status="item.review_status"
                  showAction
                  :arrow="item.review_status !== 'completed'"
                  :items="availableStatusItems(item)"
                  @next="startQaCheck(item)"
                  @selected="holdQaCheck(item)"
                />
              </template>

              <template #problems="{ item }">
                <span>{{ item.task_work_type_name }}</span>
              </template>

              <template #checked="{ item }">
                <span>{{ item.checked_by_name || "-" }}</span>
              </template>

              <template #actions="{ item }">
                <div class="action">
                  <div
                    v-if="item.result"
                    class="icon"
                    :class="item.result"
                    v-tooltip="{
                      content: item.comment,
                      html: true,
                    }"
                  >
                    <Check v-if="item.result == 'passed'" :size="16" />

                    <X v-if="item.result == 'failed'" :size="16" />
                  </div>

                  <div v-if="item.task_snapshot.eld_link" class="icon">
                    <LinkExternal02
                      :size="16"
                      @click="(e: any) => goLink(e, item)"
                    />
                  </div>

                  <div v-if="item.review_status != 'completed'" class="icon">
                    <Trash01
                      :size="16"
                      @click="(e: any) => deleteReview(e, item)"
                    />
                  </div>
                </div>
              </template>
            </CommonTable>
          </template>
        </template>
      </template>

      <div ref="sentinel" class="v-tasks__sentinel"></div>

      <div v-if="isLoadingMore" class="v-tasks__loading-more">
        Loading more…
      </div>
      <div
        v-else-if="!hasMore && reviews.length && !loading"
        class="v-tasks__end"
      >
        No more tasks
      </div>
    </div>

    <CommonModal
      v-model="isModalVisible"
      title="Generate tasks"
      @close="closeModal"
    >
      <CustomTab :tabs="tabs" v-model="activeTab" isAligned />

      <div v-if="modalItem" class="modal-body">
        <CommonDropdown
          v-if="activeTab == 1"
          v-model="modalItem.shift_id"
          :items="shiftItems"
          label="Shift"
          clearable
          fullWidth
        />

        <CommonDropdown
          v-if="activeTab == 2"
          v-model="modalItem.team_id"
          :items="teamItems"
          label="Team"
          clearable
          fullWidth
        />

        <CustomSearchDropdown
          v-if="activeTab == 3"
          v-model="modalItem.user_id"
          :items="userFilterItems"
          label="User"
          position="bottom"
          clearable
          dropdownFullWidth
          fullWidth
          @search="searchFilterUser"
        />

        <CommonInput
          mask="####-##-##"
          v-model="modalItem.date"
          label="Date (YYYY-MM-DD)"
        />

        <CommonInput v-model="modalItem.count" label="Task count" />
      </div>

      <template #footer>
        <div
          style="
            display: flex;
            justify-content: flex-end;
            width: 100%;
            gap: 16px;
          "
        >
          <CommonButton
            type="primary"
            label="Save"
            @clicked="save"
            :loading="loading"
            :isDisabled="loading"
          />
          <CommonButton
            type="outline"
            label="Back"
            @clicked="closeModal"
            :loading="loading"
            :isDisabled="loading"
          />
        </div>
      </template>
    </CommonModal>

    <CommonModal
      v-model="isCompleteModalVisible"
      title="Complete review"
      @close="closeCompleteModal"
    >
      <div class="body" v-if="completeModalItem">
        <div class="header">
          <div class="correct" @click="completeModalItem.isCorrect = true">
            <div v-if="completeModalItem.isCorrect" class="header__icon">
              <Check :size="14" />
            </div>
            <div v-else class="header__icon-place"></div>
            <div class="label">Correct</div>
          </div>
          <div class="incorrect" @click="completeModalItem.isCorrect = false">
            <div v-if="!completeModalItem.isCorrect" class="header__icon">
              <X :size="14" />
            </div>
            <div v-else class="header__icon-place"></div>
            <div class="label">Incorrect</div>
          </div>
        </div>

        <CommonInput
          v-model="completeModalItem.comment"
          label="Comment"
          multiline
          :error="
            completeModalItem.isCorrect === false &&
            !completeModalItem?.comment?.trim()
          "
          :errorMessage="'Comment is required'"
        />
      </div>

      <template #footer>
        <div
          style="
            display: flex;
            justify-content: flex-end;
            width: 100%;
            gap: 16px;
          "
        >
          <CommonButton
            type="primary"
            label="Save"
            :loading="loading"
            :is-disabled="loading"
            @clicked="saveCompleteModal"
          />
          <CommonButton
            type="outline"
            label="Back"
            :loading="loading"
            :is-disabled="loading"
            @clicked="closeCompleteModal"
          />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style lang="scss" scoped>
.v-check {
  padding: var(--base-space-24);
  display: flex;
  flex-direction: column;
  gap: var(--base-space-24);
  background: var(--color-system-background);
  height: calc(100vh - 80px);
  overflow: auto;

  &__header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }

  .date-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--base-space-8);
    padding: 10px 16px;
    cursor: pointer;
    user-select: none;
    border-top-left-radius: var(--base-radius-16);
    border-top-right-radius: var(--base-radius-16);
    transition: background-color 0.15s;
    background: var(--color-secondary-system-background);
    margin-top: 10px;
    height: 40px;

    &.is-collapsed {
      border-radius: var(--base-radius-16);
    }
  }

  .date-chevron {
    display: flex;
    align-items: center;
    color: var(--color-label-secondary);
    transition: transform 0.2s;

    &.is-collapsed {
      transform: rotate(-180deg);
    }
  }

  .date-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-label);
  }

  &__sentinel {
    height: 1px;
  }

  &__loading-more,
  &__end {
    text-align: center;
    padding: var(--base-space-16);
    font-size: 13px;
    color: var(--color-label-secondary);
  }

  :deep(.c-co-table) {
    margin-top: 0 !important;
  }

  :deep(.table thead th) {
    &:first-child {
      border-top-left-radius: 0 !important;
    }

    &:last-child {
      border-top-right-radius: 0 !important;
    }
  }
}

.qa-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  border-radius: 8px;

  &.PASSED {
    background: var(--color-green-light);
  }

  &.FAILED {
    background: var(--color-red-light);
  }

  &.SKIPPED {
    background: var(--color-orange-light);
  }
}

.company__name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;

  .app {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: var(--color-label-secondary);
    white-space: nowrap;
  }
}
.action {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon {
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  background: var(--color-secondary-system-background);

  &.passed {
    background: var(--color-system-green);
    color: var(--color-white);
  }

  &.failed {
    background: var(--color-system-red);
    color: var(--color-white);
  }
}

.modal-body {
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;

  .correct,
  .incorrect {
    border: 1px solid var(--color-border);
    border-radius: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    flex: 1;
    cursor: pointer;
    color: var(--color-label);
  }

  .correct,
  .incorrect {
    .header__icon {
      width: 18px;
      height: 18px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      color: var(--color-white);
    }
  }

  .correct {
    .header__icon {
      background: var(--color-primary);
    }
  }

  .incorrect {
    .header__icon {
      background: var(--color-system-red);
    }
  }

  .header__icon-place {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1px solid var(--color-border);
  }
}
</style>
